import { PropsWithChildren, useContext } from 'react';

import { demoClusterSecurityInsightsBestPractices } from '@cast/fixtures';

import { useIsSecurityDemoMode } from '@cast/infrastructure-demo';
import { InitiateTour, TourStep } from 'core/tour';
import { OrganizationContainer } from '@cast/shared-organization-ui';
import { ChecksPassed } from 'features/security-insights';
import { useBestPracticeOverviewQuery } from 'hooks/queries/security-insights';

import { ChecksFilters } from './_components/ChecksFilters';
import { ExportCsvButton } from './_components/ExportCsvButton';
import { NoData } from './_components/NoData';
import { useGetBestPracticeChecks } from './_hooks/useGetBestPracticeChecks';
import {
  ChecksTable,
  ComplianceStandard,
  ComplianceStandardsLoading,
} from './components';
import { checksTableUrlKey } from './constants';
import { useChecksQueryFilters, useComplianceCheckCriteria } from './hooks';
import {
  complianceTourId,
  complianceTourSteps,
  complianceTourStepsOrder,
} from '../_tours/complianceTour';
import { securityActionButtonStep } from '../_tours/securityTourAction';
import { SecurityPageContainer, SecurityPageHeader } from '../components';
import { useGetBestPracticeComplianceStandards } from '../hooks/useGetBestPracticeComplianceStandards';
import { useHasNoAgentsEnabled } from '../hooks/useHasNoAgentsEnabled';
import { useSecurityDataOrFixture } from '../hooks/useSecurityDataOrFixture';
import { useSharedFilters } from '../hooks/useSharedFilters';
import {
  SecurityDemoProvider,
  SecurityDemoProviderContext,
} from '../SecurityDemoProvider';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  SearchProvider,
  useSearchContext,
  UseServerData,
} from '@cast/shared-search-ui';

import { Typography, Box, Stack } from '@cast/design-system';
import type { StackProps } from '@cast/design-system';

const useData: UseServerData = ({ values, enabled }) => {
  const { isLoading: isLoadingStandards } =
    useGetBestPracticeComplianceStandards();

  const filters = useChecksQueryFilters(values);
  const { checks, isLoading, error, refetch } = useGetBestPracticeChecks({
    filters,
    enabled: enabled && !isLoadingStandards,
  });
  const { isLoading: isLoadingOverview } = useBestPracticeOverviewQuery({
    standard: filters.standard,
  });

  const data = useSecurityDataOrFixture(checks, () =>
    demoClusterSecurityInsightsBestPractices().then((data) => data.checks)
  );

  return {
    data,
    isLoading: isLoading || isLoadingOverview || isLoadingStandards,
    error,
    hasFiltersApplied: true,
    refetch,
  };
};

const Container = ({ children, ...props }: PropsWithChildren<StackProps>) => {
  const searchCtx = useSearchContext();
  const { isSecurityDemoContentVisible } = useContext(
    SecurityDemoProviderContext
  );

  return (
    <OrganizationContainer>
      <Stack height="100%" pb={40} gap={24}>
        <SecurityPageHeader />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography variant="h3" data-testid="page-header">
            Compliance
            <InitiateTour
              type={complianceTourId}
              steps={complianceTourStepsOrder()}
              message="Check the results of cluster and workload configuration scanning."
              globalTourStepProps={securityActionButtonStep}
              sx={{ position: 'absolute', display: 'inline-block' }}
              forceVisible={isSecurityDemoContentVisible}
            />
          </Typography>
        </Stack>
        <SecurityPageContainer isLoading={searchCtx?.isLoading} {...props}>
          {children}
        </SecurityPageContainer>
      </Stack>
    </OrganizationContainer>
  );
};

const CompliancesInner = () => {
  const searchCtx = useSearchContext();
  const { hasNoAgentsEnabled } = useHasNoAgentsEnabled();
  const {
    overview,
    isLoading: isLoadingOverview,
    error: overviewError,
    refetch: refetchOverview,
  } = useBestPracticeOverviewQuery({
    standard: searchCtx?.values?.standard,
  });

  useSharedFilters();

  const isSecurityDemo = useIsSecurityDemoMode();

  if (overviewError) {
    return (
      <Box mt={40}>
        <FailedToLoad
          title="Failed to load security report"
          refresh={refetchOverview}
          testId="compliance-failed-to-load"
        />
      </Box>
    );
  }

  const showNoData =
    !isLoadingOverview &&
    !overview?.failedChecks &&
    !overview?.passedChecks &&
    !hasNoAgentsEnabled;

  if (showNoData) {
    return (
      <Container pt={56}>
        <NoData />
      </Container>
    );
  }

  const showChecksPassed =
    !isLoadingOverview && overview?.failedChecks === 0 && !hasNoAgentsEnabled;

  if (showChecksPassed) {
    return (
      <Container>
        <ChecksPassed total={overview?.passedChecks} />
      </Container>
    );
  }

  return (
    <Container flexGrow={1} gap={24}>
      <Stack direction="row" alignItems="center" gap={12}>
        <ComplianceStandard />
        <TourStep {...complianceTourSteps.complianceStandard} />
      </Stack>
      <Stack gap={20} flexGrow={1}>
        {!isSecurityDemo && <ChecksFilters />}
        <ChecksTable tableActions={<ExportCsvButton />} />
      </Stack>
    </Container>
  );
};

export const Compliances = () => {
  const criteria = useComplianceCheckCriteria();
  return (
    <SecurityDemoProvider>
      <ComplianceStandardsLoading>
        <SearchProvider
          useData={useData}
          criteria={criteria}
          urlKey={checksTableUrlKey}
        >
          <CompliancesInner />
        </SearchProvider>
      </ComplianceStandardsLoading>
    </SecurityDemoProvider>
  );
};
